import { Fragment, useState, useCallback } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ActionsMenu from '../../components/common/ActionsMenu';
import Modal from '../../components/common/Modal';
import { ADMIN_CONVERSION_JOBS_QUERY, ADMIN_PROJECTS_QUERY } from '../../graphql/queries';
import { ADMIN_RETRANSLATE_MUTATION, ADMIN_SYNC_JOB_STATUS_MUTATION, ADMIN_RETRIGGER_CONVERSION_MUTATION, ADMIN_OVERRIDE_JOB_STATUS_MUTATION, ADMIN_DUPLICATE_FILE_MUTATION } from '../../graphql/mutations';
import styles from './AdminConversionJobs.module.css';

interface ConversionJob {
  id: string;
  originalName: string;
  extension: string;
  fileSize: number;
  type: 'two_d' | 'three_d' | 'point_cloud' | 'gaussian' | 'orthomosaic' | 'video360';
  status: string;
  apsStatus: string | null;
  apsUrn: string | null;
  svStatus: string | null;
  potreePath: string | null;
  batchJobId: string | null;
  batchJobName: string | null;
  errorMessage: string | null;
  project: { id: string; name: string } | null;
  uploadedBy: { id: string; firstName: string; lastName: string } | null;
  createdAt: string;
  updatedAt: string;
}

interface ProjectGroup {
  projectId: string;
  projectName: string;
  jobs: ConversionJob[];
}

const FILE_TYPE_TABS = [
  { value: '', label: '2D / 3D' },
  { value: 'point_cloud', label: 'Point Cloud' },
  { value: 'gaussian', label: 'Gaussian Splats' },
  { value: 'orthomosaic', label: 'Orthomosaic' },
  { value: 'video360', label: '360 Videos' },
];

const TYPE_ICON: Record<ConversionJob['type'], string> = {
  two_d: 'description', three_d: 'view_in_ar', point_cloud: 'point_scan', gaussian: 'blur_on',
  orthomosaic: 'satellite_alt', video360: 'panorama_photosphere',
};
const TYPE_CHIP: Record<ConversionJob['type'], string> = {
  two_d: '2D', three_d: '3D', point_cloud: 'PC', gaussian: 'GS', orthomosaic: 'ORTHO', video360: '360',
};

// The status shown for a 2D/3D row: the self-hosted pipeline (dwg2svg/ifc2glb,
// S3-derived svStatus) when the file has one, the APS translation otherwise.
const jobStatus = (job: { svStatus: string | null; apsStatus: string | null }) =>
  job.svStatus ?? job.apsStatus;

// The self-hosted status is derived from S3, so on those rows the APS status is
// a secondary line: RVT's paid RVT→IFC step, or a legacy APS translation.
const apsLabel = (job: { svStatus: string | null; extension: string }) =>
  job.svStatus == null ? 'APS' : job.extension.toLowerCase() === 'rvt' ? 'RVT→IFC (APS)' : 'Legacy APS';

const APS_STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'none', label: 'Not Started' },
  { value: 'inprogress', label: 'In Progress' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
];

const PC_STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'processing', label: 'Processing' },
  { value: 'ready', label: 'Ready' },
  { value: 'error', label: 'Error' },
];

const STATUS_CONFIG: Record<string, { label: string; dotClass: string; badgeClass: string; pulse?: boolean }> = {
  none:       { label: 'Not started', dotClass: 'bg-gray-400',   badgeClass: styles.statusNone },
  pending:    { label: 'Pending',     dotClass: 'bg-yellow-400', badgeClass: styles.statusPending },
  inprogress: { label: 'In progress', dotClass: 'bg-blue-500',   badgeClass: styles.statusInprogress, pulse: true },
  success:    { label: 'Success',     dotClass: 'bg-green-500',  badgeClass: styles.statusSuccess },
  failed:     { label: 'Failed',      dotClass: 'bg-red-500',    badgeClass: styles.statusFailed },
  processing: { label: 'Processing',  dotClass: 'bg-blue-500',   badgeClass: styles.statusInprogress, pulse: true },
  ready:      { label: 'Ready',       dotClass: 'bg-green-500',  badgeClass: styles.statusSuccess },
  error:      { label: 'Error',       dotClass: 'bg-red-500',    badgeClass: styles.statusFailed },
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (s: string) => new Date(s).toLocaleString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

function StatusBadge({ status }: { status: string | null }) {
  const key = status ?? 'none';
  const cfg = STATUS_CONFIG[key] || STATUS_CONFIG.none;
  return (
    <span className={`${styles.statusBadge} ${cfg.badgeClass}`}>
      <span className={`${styles.dot} ${cfg.dotClass} ${cfg.pulse ? styles.dotPulse : ''}`} />
      {cfg.label}
    </span>
  );
}

function groupByProject(jobs: ConversionJob[]): ProjectGroup[] {
  const map = new Map<string, ProjectGroup>();
  for (const job of jobs) {
    const key = job.project?.id ?? '__none__';
    if (!map.has(key)) {
      map.set(key, {
        projectId: key,
        projectName: job.project?.name ?? 'No Project',
        jobs: [],
      });
    }
    map.get(key)!.jobs.push(job);
  }
  return Array.from(map.values());
}

const PROJECTS_PER_PAGE = 10;

const AdminConversionJobs = () => {
  const [search, setSearch]             = useState('');
  const [fileTypeTab, setFileTypeTab]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [projectPage, setProjectPage]   = useState(1);

  const [showNewJob, setShowNewJob]     = useState(false);
  const [jobSearch, setJobSearch]       = useState('');
  const [selectedFile, setSelectedFile] = useState<ConversionJob | null>(null);
  const [syncingId, setSyncingId]       = useState<string | null>(null);
  const [retriggeringId, setRetriggeringId] = useState<string | null>(null);
  const [retriggeringProjectId, setRetriggeringProjectId] = useState<string | null>(null);
  const [expandedError, setExpandedError]   = useState<string | null>(null);
  const [collapsedProjects, setCollapsedProjects] = useState<Record<string, boolean>>({});

  const [overrideJob, setOverrideJob]       = useState<ConversionJob | null>(null);
  const [overrideStatus, setOverrideStatus] = useState('');
  const [overrideError, setOverrideError]   = useState<string | null>(null);

  const [duplicateJob, setDuplicateJob]               = useState<ConversionJob | null>(null);
  const [duplicateGroup, setDuplicateGroup]           = useState<ProjectGroup | null>(null);
  const [duplicatingAll, setDuplicatingAll]           = useState(false);
  const [duplicateAllProgress, setDuplicateAllProgress] = useState<{ done: number; total: number } | null>(null);
  const [duplicateProjectSearch, setDuplicateProjectSearch] = useState('');
  const [duplicateTargetProjectId, setDuplicateTargetProjectId] = useState<string | null>(null);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  const isPointCloudTab = fileTypeTab === 'point_cloud';
  const isGaussianTab = fileTypeTab === 'gaussian';
  const isBatchTab = fileTypeTab === 'orthomosaic' || fileTypeTab === 'video360';
  // Point cloud, gaussian, orthomosaic and 360 video rows live on File.status
  // + the Batch retrigger mutation; only 2D/3D rows use apsStatus/svStatus + APS actions.
  const usesFileStatus = isPointCloudTab || isGaussianTab || isBatchTab;
  const statusTabs = usesFileStatus ? PC_STATUS_TABS : APS_STATUS_TABS;

  const client = useApolloClient();

  const { data, loading, error, refetch } = useQuery(ADMIN_CONVERSION_JOBS_QUERY, {
    variables: {
      page: 1,
      limit: 1000,
      status: statusFilter || undefined,
      fileType: fileTypeTab || undefined,
      search: search || undefined,
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: pickData, loading: pickLoading } = useQuery(ADMIN_CONVERSION_JOBS_QUERY, {
    variables: { limit: 20, fileType: fileTypeTab || undefined, search: jobSearch || undefined },
    skip: !showNewJob,
    fetchPolicy: 'network-only',
  });

  const [retranslate, { loading: retranslating }] = useMutation(ADMIN_RETRANSLATE_MUTATION, {
    onCompleted: () => { setShowNewJob(false); setSelectedFile(null); setJobSearch(''); refetch(); },
    onError: (err) => alert(err.message),
  });

  const [retriggerConversion] = useMutation(ADMIN_RETRIGGER_CONVERSION_MUTATION, {
    onCompleted: () => { setRetriggeringId(null); refetch(); },
    onError: (err) => { setRetriggeringId(null); alert(err.message); },
  });

  const [syncStatus] = useMutation(ADMIN_SYNC_JOB_STATUS_MUTATION, {
    onCompleted: () => { setSyncingId(null); refetch(); },
    onError: (err) => { setSyncingId(null); alert(err.message); },
  });

  const [overrideJobStatus, { loading: overriding }] = useMutation(ADMIN_OVERRIDE_JOB_STATUS_MUTATION, {
    onCompleted: () => { setOverrideJob(null); setOverrideStatus(''); refetch(); },
    onError: (err) => setOverrideError(err.message),
  });

  const { data: projectListData, loading: projectListLoading } = useQuery(ADMIN_PROJECTS_QUERY, {
    variables: { search: duplicateProjectSearch || undefined, limit: 50 },
    skip: !duplicateJob && !duplicateGroup,
    fetchPolicy: 'network-only',
  });

  const [duplicateFile, { loading: duplicating }] = useMutation(ADMIN_DUPLICATE_FILE_MUTATION, {
    onCompleted: () => {
      setDuplicateJob(null);
      setDuplicateProjectSearch('');
      setDuplicateTargetProjectId(null);
      refetch();
    },
    onError: (err) => setDuplicateError(err.message),
  });

  const handleRetranslate = useCallback((fileId: string) => {
    retranslate({ variables: { fileId } });
  }, [retranslate]);

  const handleRetrigger = useCallback((fileId: string) => {
    setRetriggeringId(fileId);
    retriggerConversion({ variables: { fileId } });
  }, [retriggerConversion]);

  const handleSync = useCallback((fileId: string) => {
    setSyncingId(fileId);
    syncStatus({ variables: { fileId } });
  }, [syncStatus]);

  // Re-run a self-hosted conversion (dwg2svg / ifc2glb / rvt2ifc re-mesh) through
  // the same REST endpoints the viewers use; force=1 clears the previous output.
  const [svRetryingId, setSvRetryingId] = useState<string | null>(null);
  const handleSvRetry = async (job: ConversionJob) => {
    const ext = job.extension.toLowerCase();
    const base = ext === 'dwg' || ext === 'dxf' ? 'dwg2d' : ext === 'rvt' ? 'rvt2ifc' : 'ifc3d';
    setSvRetryingId(job.id);
    try {
      const res = await fetch(`/api/${base}/translate/${job.id}?force=1`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('geodraft_token') || ''}` },
      });
      if (!res.ok) {
        let reason = `HTTP ${res.status}`;
        try {
          reason = ((await res.json()) as { error?: string }).error || reason;
        } catch {
          // Not a JSON body: the HTTP status is all there is to report.
        }
        alert(`Could not re-run ${job.originalName}: ${reason}`);
      }
    } catch (err) {
      alert(`Could not re-run ${job.originalName}: ${(err as Error).message}`);
    }
    try {
      await refetch();
    } catch {
      // A failed reload already shows as the jobs query's own error.
    } finally {
      setSvRetryingId(null);
    }
  };

  const handleTriggerAll = async (group: ProjectGroup) => {
    setRetriggeringProjectId(group.projectId);
    // In-flight gaussian trainings are refused server-side (double-submit
    // guard) — skip them instead of collecting a refusal per row.
    const targets = isGaussianTab || isBatchTab
      ? group.jobs.filter(job => job.status !== 'processing' && job.status !== 'pending')
      : group.jobs;
    const failed: string[] = [];
    try {
      await Promise.all(
        targets.map(async job => {
          try {
            await client.mutate({
              mutation: usesFileStatus ? ADMIN_RETRIGGER_CONVERSION_MUTATION : ADMIN_RETRANSLATE_MUTATION,
              variables: { fileId: job.id },
            });
          } catch (err) {
            failed.push(`${job.originalName}: ${(err as Error).message}`);
          }
        })
      );
    } finally {
      setRetriggeringProjectId(null);
      refetch();
    }
    if (failed.length > 0) {
      alert(`${failed.length} of ${targets.length} job${targets.length !== 1 ? 's' : ''} could not be started:\n\n${failed.join('\n')}`);
    }
  };

  const closeDuplicateAll = () => {
    setDuplicateGroup(null);
    setDuplicateProjectSearch('');
    setDuplicateTargetProjectId(null);
    setDuplicateAllProgress(null);
  };

  const handleDuplicateAll = async () => {
    if (!duplicateGroup || !duplicateTargetProjectId) return;
    const jobs = duplicateGroup.jobs;
    setDuplicatingAll(true);
    setDuplicateAllProgress({ done: 0, total: jobs.length });
    // Sequential, not Promise.all: each duplicate triggers a server-side S3 copy
    // (+ a Batch submit for point clouds). Running them one at a time avoids a
    // burst of concurrent copies/submits and lets us show real progress.
    // Continue past per-file failures so one bad file doesn't abort the rest;
    // report any failures at the end.
    let done = 0;
    const failed: string[] = [];
    for (const job of jobs) {
      try {
        await client.mutate({
          mutation: ADMIN_DUPLICATE_FILE_MUTATION,
          variables: { fileId: job.id, targetProjectId: duplicateTargetProjectId },
        });
      } catch {
        failed.push(job.originalName);
      }
      done += 1;
      setDuplicateAllProgress({ done, total: jobs.length });
    }
    setDuplicatingAll(false);
    closeDuplicateAll();
    refetch();
    if (failed.length > 0) {
      alert(`Duplicated ${jobs.length - failed.length} of ${jobs.length} files.\n\nFailed:\n${failed.join('\n')}`);
    }
  };

  const toggleProject = (projectId: string) => {
    setCollapsedProjects(prev => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  const jobs: ConversionJob[] = data?.adminConversionJobs?.jobs ?? [];
  const allGroups   = groupByProject(jobs);
  const totalGroups = allGroups.length;
  const totalPages  = Math.ceil(totalGroups / PROJECTS_PER_PAGE);
  const groups      = allGroups.slice(
    (projectPage - 1) * PROJECTS_PER_PAGE,
    projectPage * PROJECTS_PER_PAGE,
  );

  const pickJobs: ConversionJob[] = pickData?.adminConversionJobs?.jobs ?? [];

  return (
    <DashboardLayout onSearch={(q) => { setSearch(q); setProjectPage(1); }} searchPlaceholder="Search by file name...">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-end flex-wrap gap-3">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Conversion Jobs</h2>
            <p className="text-on-surface-variant mt-1">
              {isGaussianTab
                ? 'Gaussian splat GPU training jobs — restart re-runs the training from the stored panoramas and octree.'
                : 'APS translation jobs for all 2D and 3D files on the platform.'}
            </p>
          </div>
          <button
            onClick={() => setShowNewJob(true)}
            className="btn-primary px-5 py-2.5 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-sm">add</span>
            New Job
          </button>
        </div>

        {/* File type tabs */}
        <div className="flex items-center flex-wrap gap-1 bg-surface-container rounded-xl p-1.5 w-fit">
          {FILE_TYPE_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => { setFileTypeTab(tab.value); setStatusFilter(''); setProjectPage(1); }}
              className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                fileTypeTab === tab.value
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Status filter tabs */}
        <div className="flex items-center flex-wrap gap-1 bg-surface-container-low rounded-xl p-1.5 w-fit">
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => { setStatusFilter(tab.value); setProjectPage(1); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                statusFilter === tab.value
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl overflow-hidden">
          {loading && jobs.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-on-surface-variant">
              <span className="material-symbols-outlined animate-spin mr-2">refresh</span>
              Loading jobs...
            </div>
          ) : error && jobs.length === 0 ? (
            <div className="py-16 text-center">
              <span className="material-symbols-outlined text-4xl text-error mb-3 block">cloud_off</span>
              <p className="text-on-surface-variant">Failed to load conversion jobs</p>
              <p className="text-xs text-on-surface-variant mt-1">{error.message}</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="py-16 text-center">
              <span className="material-symbols-outlined text-4xl text-outline mb-3 block">transform</span>
              <p className="text-on-surface-variant">No conversion jobs found</p>
            </div>
          ) : (
            <>
              <div className={styles.tableWrap}>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface-container-low">
                    <th className="table-header">File</th>
                    <th className="table-header">Type</th>
                    <th className="table-header">Size</th>
                    <th className="table-header">{isGaussianTab ? 'Training Status' : isPointCloudTab ? 'Potree Status' : isBatchTab ? 'Conversion Status' : 'APS Status'}</th>
                    <th className="table-header">Updated</th>
                    <th className="table-header text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {groups.map((group) => {
                    const isCollapsed = !!collapsedProjects[group.projectId];
                    const isBusy = retriggeringProjectId === group.projectId;
                    return (
                      <Fragment key={group.projectId}>
                        {/* Project group header */}
                        <tr className={styles.projectHeaderRow}>
                          <td colSpan={6} className={styles.projectHeaderCell}>
                            <div className={styles.projectHeaderInner}>
                              <button
                                className={styles.projectCollapseBtn}
                                onClick={() => toggleProject(group.projectId)}
                                title={isCollapsed ? 'Expand' : 'Collapse'}
                              >
                                <span
                                  className="material-symbols-outlined"
                                  style={{ fontSize: 16, transition: 'transform 0.15s', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                                >
                                  expand_more
                                </span>
                              </button>
                              <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#6366f1' }}>folder_special</span>
                              <span className={styles.projectName}>{group.projectName}</span>
                              <span className={styles.projectCount}>{group.jobs.length} job{group.jobs.length !== 1 ? 's' : ''}</span>
                              {group.projectId !== '__none__' && !isGaussianTab && (
                                <button
                                  className={styles.duplicateAllBtn}
                                  onClick={() => {
                                    setDuplicateGroup(group);
                                    setDuplicateProjectSearch('');
                                    setDuplicateTargetProjectId(null);
                                    setDuplicateAllProgress(null);
                                  }}
                                  disabled={isBusy}
                                  title="Duplicate all files in this project to another project"
                                >
                                  <span className="material-symbols-outlined" style={{ fontSize: 14 }}>content_copy</span>
                                  Duplicate All
                                </button>
                              )}
                              <button
                                className={`${styles.triggerAllBtn} ${isBusy ? styles.triggerAllBtnBusy : ''}`}
                                onClick={() => handleTriggerAll(group)}
                                disabled={isBusy}
                                title={isGaussianTab ? 'Restart all trainings in this project' : usesFileStatus ? 'Retrigger all jobs in this project' : 'Translate all files in this project'}
                              >
                                <span className={`material-symbols-outlined ${isBusy ? 'animate-spin' : ''}`} style={{ fontSize: 14 }}>
                                  {isBusy ? 'refresh' : usesFileStatus ? 'replay' : 'play_arrow'}
                                </span>
                                {isBusy ? 'Running…' : isGaussianTab ? 'Restart All' : usesFileStatus ? 'Retrigger All' : 'Translate All'}
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Job rows */}
                        {!isCollapsed && group.jobs.map((job) => (
                          <Fragment key={job.id}>
                            <tr className="table-row group">
                              <td className="px-6 py-4" style={{ paddingLeft: 40 }}>
                                <div className="flex items-center gap-2">
                                  <span className="material-symbols-outlined text-base text-on-surface-variant">
                                    {TYPE_ICON[job.type] ?? 'insert_drive_file'}
                                  </span>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-on-surface truncate max-w-[260px]" title={job.originalName}>
                                      {job.originalName}
                                    </p>
                                    <p className="text-[10px] text-on-surface-variant uppercase">.{job.extension}</p>
                                    {job.batchJobName && (
                                      <p
                                        className={styles.batchJobLine}
                                        title={job.batchJobId ? `Batch job ID: ${job.batchJobId}` : undefined}
                                        onClick={() => navigator.clipboard?.writeText(job.batchJobName!)}
                                      >
                                        <span className="material-symbols-outlined" style={{ fontSize: 11 }}>memory</span>
                                        <span className="truncate">{job.batchJobName}</span>
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-[10px] font-bold uppercase text-on-surface-variant bg-surface-container px-2 py-0.5 rounded">
                                  {TYPE_CHIP[job.type] ?? job.type}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-xs text-on-surface-variant">
                                {formatSize(job.fileSize)}
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex flex-col gap-1">
                                  <StatusBadge status={usesFileStatus ? job.status : jobStatus(job)} />
                                  {!isPointCloudTab && job.svStatus != null && job.apsStatus && (
                                    <span className="text-[10px] text-on-surface-variant">{apsLabel(job)}: {job.apsStatus}</span>
                                  )}
                                  {job.errorMessage && (
                                    <button
                                      onClick={() => setExpandedError(expandedError === job.id ? null : job.id)}
                                      className="text-[10px] text-error flex items-center gap-0.5 hover:underline"
                                    >
                                      <span className="material-symbols-outlined text-xs">error</span>
                                      {expandedError === job.id ? 'Hide error' : 'View error'}
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-xs text-on-surface-variant">
                                {formatDate(job.updatedAt)}
                              </td>
                              <td className="px-6 py-4 text-right">
                                <ActionsMenu
                                  label={`Actions for ${job.originalName}`}
                                  actions={[
                                    usesFileStatus ? {
                                      label: isGaussianTab ? 'Restart GPU training' : isBatchTab ? 'Re-run the conversion' : 'Retrigger Batch conversion',
                                      icon: 'replay', busy: retriggeringId === job.id, disabled: retriggeringId === job.id,
                                      onSelect: () => handleRetrigger(job.id),
                                    } : {
                                      label: 'Run / Re-run APS translation', icon: 'play_arrow', disabled: retranslating,
                                      onSelect: () => handleRetranslate(job.id),
                                    },
                                    !usesFileStatus && job.svStatus != null && {
                                      label: 'Re-run conversion', icon: 'replay', hint: 'Clears the previous output',
                                      busy: svRetryingId === job.id, disabled: svRetryingId === job.id,
                                      onSelect: () => handleSvRetry(job),
                                    },
                                    !usesFileStatus && !!job.apsUrn && {
                                      label: 'Sync status from APS', icon: 'sync', busy: syncingId === job.id, disabled: syncingId === job.id,
                                      onSelect: () => handleSync(job.id),
                                    },
                                    {
                                      label: 'Override status', icon: 'edit',
                                      onSelect: () => {
                                        setOverrideJob(job);
                                        setOverrideStatus(usesFileStatus ? job.status : (job.apsStatus ?? 'none'));
                                        setOverrideError(null);
                                      },
                                    },
                                    !isGaussianTab && {
                                      label: 'Duplicate file to another project', icon: 'content_copy',
                                      onSelect: () => {
                                        setDuplicateJob(job);
                                        setDuplicateProjectSearch('');
                                        setDuplicateTargetProjectId(null);
                                        setDuplicateError(null);
                                      },
                                    },
                                  ]}
                                />
                              </td>
                            </tr>
                            {expandedError === job.id && job.errorMessage && (
                              <tr>
                                <td colSpan={6} className="px-6 pb-4" style={{ paddingLeft: 40 }}>
                                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 font-mono whitespace-pre-wrap break-all">
                                    {job.errorMessage}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-outline-variant/10">
                  <button
                    onClick={() => setProjectPage((p) => Math.max(1, p - 1))}
                    disabled={projectPage === 1}
                    className="flex items-center gap-1 text-sm font-medium text-on-surface-variant hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="material-symbols-outlined text-lg">chevron_left</span>
                    Previous
                  </button>
                  <span className="text-sm text-on-surface-variant">
                    Page {projectPage} of {totalPages} · {totalGroups} projects · {jobs.length} jobs
                  </span>
                  <button
                    onClick={() => setProjectPage((p) => Math.min(totalPages, p + 1))}
                    disabled={projectPage >= totalPages}
                    className="flex items-center gap-1 text-sm font-medium text-on-surface-variant hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <span className="material-symbols-outlined text-lg">chevron_right</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Duplicate file modal */}
      <Modal
        isOpen={!!duplicateJob}
        onClose={() => { setDuplicateJob(null); setDuplicateProjectSearch(''); setDuplicateTargetProjectId(null); setDuplicateError(null); }}
        title="Duplicate File to Another Project"
        footer={
          <>
            <button
              onClick={() => { setDuplicateJob(null); setDuplicateProjectSearch(''); setDuplicateTargetProjectId(null); setDuplicateError(null); }}
              className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (!duplicateJob || !duplicateTargetProjectId) return;
                setDuplicateError(null);
                duplicateFile({ variables: { fileId: duplicateJob.id, targetProjectId: duplicateTargetProjectId } });
              }}
              disabled={!duplicateTargetProjectId || duplicating}
              className="btn-primary px-5 py-2 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container disabled:opacity-50 flex items-center gap-2"
            >
              <span className={`material-symbols-outlined text-sm ${duplicating ? 'animate-spin' : ''}`}>
                {duplicating ? 'refresh' : 'content_copy'}
              </span>
              {duplicating ? 'Duplicating…' : 'Duplicate'}
            </button>
          </>
        }
      >
        {duplicateJob && (
          <div className="space-y-4">
            <div className="bg-surface-container rounded-lg p-3 text-sm">
              <p className="font-medium text-on-surface truncate">{duplicateJob.originalName}</p>
              <p className="text-xs text-on-surface-variant mt-0.5">
                From: {duplicateJob.project?.name ?? '—'} · .{duplicateJob.extension} · {formatSize(duplicateJob.fileSize)}
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Target project</label>
              <input
                className="input-field"
                placeholder="Search projects by name..."
                value={duplicateProjectSearch}
                onChange={(e) => setDuplicateProjectSearch(e.target.value)}
                autoFocus
              />
            </div>

            <div className={styles.modalFileList}>
              {projectListLoading && (
                <div className="flex items-center justify-center py-8 text-on-surface-variant text-sm">
                  <span className="material-symbols-outlined animate-spin mr-2 text-base">refresh</span>
                  Searching...
                </div>
              )}
              {!projectListLoading && (projectListData?.adminProjects ?? []).length === 0 && (
                <div className="py-8 text-center text-sm text-on-surface-variant">
                  No projects found
                </div>
              )}
              {(projectListData?.adminProjects ?? []).map((p: { id: string; name: string; location?: string | null }) => (
                <div
                  key={p.id}
                  className={`${styles.modalFileRow} ${duplicateTargetProjectId === p.id ? styles.modalFileRowSelected : ''}`}
                  onClick={() => setDuplicateTargetProjectId(duplicateTargetProjectId === p.id ? null : p.id)}
                >
                  <span className="material-symbols-outlined text-primary text-xl">folder_special</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-on-surface truncate">{p.name}</p>
                    {p.location && <p className="text-[10px] text-on-surface-variant truncate">{p.location}</p>}
                  </div>
                  {duplicateTargetProjectId === p.id && (
                    <span className="material-symbols-outlined text-primary text-lg">check_circle</span>
                  )}
                </div>
              ))}
            </div>

            <p className="text-xs text-on-surface-variant">
              The raw S3 object will be copied server-side to the new file's location. {duplicateJob.type === 'point_cloud' ? 'A new AWS Batch conversion job will be triggered automatically.' : 'The new file will be ready immediately; APS translation runs on first viewer open.'}
            </p>
            {duplicateError && <p className={styles.dialogError}>{duplicateError}</p>}
          </div>
        )}
      </Modal>

      {/* Duplicate ALL files in a project modal */}
      <Modal
        isOpen={!!duplicateGroup}
        onClose={() => { if (!duplicatingAll) closeDuplicateAll(); }}
        title="Duplicate All Files to Another Project"
        footer={
          <>
            <button
              onClick={() => { if (!duplicatingAll) closeDuplicateAll(); }}
              disabled={duplicatingAll}
              className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDuplicateAll}
              disabled={!duplicateTargetProjectId || duplicatingAll}
              className="btn-primary px-5 py-2 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container disabled:opacity-50 flex items-center gap-2"
            >
              <span className={`material-symbols-outlined text-sm ${duplicatingAll ? 'animate-spin' : ''}`}>
                {duplicatingAll ? 'refresh' : 'content_copy'}
              </span>
              {duplicatingAll
                ? `Duplicating ${duplicateAllProgress?.done ?? 0}/${duplicateAllProgress?.total ?? 0}…`
                : `Duplicate ${duplicateGroup?.jobs.length ?? 0} file${(duplicateGroup?.jobs.length ?? 0) !== 1 ? 's' : ''}`}
            </button>
          </>
        }
      >
        {duplicateGroup && (
          <div className="space-y-4">
            <div className="bg-surface-container rounded-lg p-3 text-sm">
              <p className="font-medium text-on-surface truncate">{duplicateGroup.projectName}</p>
              <p className="text-xs text-on-surface-variant mt-0.5">
                {duplicateGroup.jobs.length} file{duplicateGroup.jobs.length !== 1 ? 's' : ''} will be copied to the selected project
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Target project</label>
              <input
                className="input-field"
                placeholder="Search projects by name..."
                value={duplicateProjectSearch}
                onChange={(e) => setDuplicateProjectSearch(e.target.value)}
                disabled={duplicatingAll}
                autoFocus
              />
            </div>

            <div className={styles.modalFileList}>
              {projectListLoading && (
                <div className="flex items-center justify-center py-8 text-on-surface-variant text-sm">
                  <span className="material-symbols-outlined animate-spin mr-2 text-base">refresh</span>
                  Searching...
                </div>
              )}
              {!projectListLoading && (projectListData?.adminProjects ?? []).length === 0 && (
                <div className="py-8 text-center text-sm text-on-surface-variant">
                  No projects found
                </div>
              )}
              {(projectListData?.adminProjects ?? [])
                .filter((p: { id: string }) => p.id !== duplicateGroup.projectId)
                .map((p: { id: string; name: string; location?: string | null }) => (
                  <div
                    key={p.id}
                    className={`${styles.modalFileRow} ${duplicateTargetProjectId === p.id ? styles.modalFileRowSelected : ''}`}
                    onClick={() => { if (!duplicatingAll) setDuplicateTargetProjectId(duplicateTargetProjectId === p.id ? null : p.id); }}
                  >
                    <span className="material-symbols-outlined text-primary text-xl">folder_special</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-on-surface truncate">{p.name}</p>
                      {p.location && <p className="text-[10px] text-on-surface-variant truncate">{p.location}</p>}
                    </div>
                    {duplicateTargetProjectId === p.id && (
                      <span className="material-symbols-outlined text-primary text-lg">check_circle</span>
                    )}
                  </div>
                ))}
            </div>

            <p className="text-xs text-on-surface-variant">
              Each file's raw S3 object is copied server-side to the new project. Point cloud files trigger a new AWS Batch conversion automatically; 2D/3D files are ready immediately. Files are duplicated one at a time — keep this dialog open until it finishes.
            </p>
          </div>
        )}
      </Modal>

      {/* Override status modal */}
      <Modal
        isOpen={!!overrideJob}
        onClose={() => { setOverrideJob(null); setOverrideStatus(''); setOverrideError(null); }}
        title="Override Job Status"
        footer={
          <>
            <button
              onClick={() => { setOverrideJob(null); setOverrideStatus(''); setOverrideError(null); }}
              className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (!overrideJob || !overrideStatus) return;
                const field = usesFileStatus ? 'status' : 'apsStatus';
                setOverrideError(null);
                overrideJobStatus({ variables: { fileId: overrideJob.id, status: overrideStatus, field } });
              }}
              disabled={!overrideStatus || overriding}
              className="btn-primary px-5 py-2 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container disabled:opacity-50 flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-sm">check</span>
              {overriding ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      >
        {overrideJob && (
          <div className="space-y-4">
            <div className="bg-surface-container rounded-lg p-3 text-sm">
              <p className="font-medium text-on-surface truncate">{overrideJob.originalName}</p>
              <p className="text-xs text-on-surface-variant mt-0.5">{overrideJob.project?.name ?? '—'}</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                New {isGaussianTab ? 'Training' : isPointCloudTab ? 'Potree' : isBatchTab ? 'Conversion' : apsLabel(overrideJob)} status
              </label>
              <select
                className="input-field"
                value={overrideStatus}
                onChange={(e) => setOverrideStatus(e.target.value)}
              >
                {(usesFileStatus ? PC_STATUS_TABS : APS_STATUS_TABS)
                  .filter((t) => t.value !== '')
                  .map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
              </select>
            </div>
            {!usesFileStatus && overrideJob.svStatus != null && (
              <p className={styles.dialogNote}>
                The status shown for this file comes from its converted output in S3, so it cannot be overridden here; use Re-run conversion instead. This only changes the {apsLabel(overrideJob)} status shown under it.
              </p>
            )}
            <p className="text-xs text-on-surface-variant">
              This directly updates the database record. It does not start or stop any background job.
            </p>
            {overrideError && <p className={styles.dialogError}>{overrideError}</p>}
          </div>
        )}
      </Modal>

      {/* New Job modal */}
      <Modal
        isOpen={showNewJob}
        onClose={() => { setShowNewJob(false); setSelectedFile(null); setJobSearch(''); }}
        title={isGaussianTab ? 'Restart Gaussian Training' : usesFileStatus ? 'Retrigger Conversion Job' : 'Run Custom Translation Job'}
        footer={
          <>
            <button
              onClick={() => { setShowNewJob(false); setSelectedFile(null); setJobSearch(''); }}
              className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (!selectedFile) return;
                if (usesFileStatus) handleRetrigger(selectedFile.id);
                else handleRetranslate(selectedFile.id);
                setShowNewJob(false); setSelectedFile(null); setJobSearch('');
              }}
              disabled={!selectedFile || retranslating || !!retriggeringId}
              className="btn-primary px-5 py-2 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container disabled:opacity-50 flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-sm">{usesFileStatus ? 'replay' : 'play_arrow'}</span>
              {retranslating || retriggeringId ? 'Starting...' : isGaussianTab ? 'Restart' : usesFileStatus ? 'Retrigger' : 'Run Translation'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Search files</label>
            <input
              className="input-field"
              placeholder="Search by file name..."
              value={jobSearch}
              onChange={(e) => setJobSearch(e.target.value)}
              autoFocus
            />
          </div>

          <div className={styles.modalFileList}>
            {pickLoading && (
              <div className="flex items-center justify-center py-8 text-on-surface-variant text-sm">
                <span className="material-symbols-outlined animate-spin mr-2 text-base">refresh</span>
                Searching...
              </div>
            )}
            {!pickLoading && pickJobs.length === 0 && (
              <div className="py-8 text-center text-sm text-on-surface-variant">
                {jobSearch ? 'No files found' : 'Start typing to search files'}
              </div>
            )}
            {pickJobs.map((job) => (
              <div
                key={job.id}
                className={`${styles.modalFileRow} ${selectedFile?.id === job.id ? styles.modalFileRowSelected : ''}`}
                onClick={() => setSelectedFile(selectedFile?.id === job.id ? null : job)}
              >
                <span className="material-symbols-outlined text-primary text-xl">
                  {TYPE_ICON[job.type] ?? 'insert_drive_file'}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-on-surface truncate">{job.originalName}</p>
                  <p className="text-[10px] text-on-surface-variant">{job.project?.name ?? '—'} · {formatSize(job.fileSize)}</p>
                </div>
                <StatusBadge status={usesFileStatus ? job.status : jobStatus(job)} />
              </div>
            ))}
          </div>

          {selectedFile && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-sm">
              <p className="font-medium text-on-surface">{selectedFile.originalName}</p>
              <p className="text-xs text-on-surface-variant mt-0.5">
                {selectedFile.project?.name} · Current status: <span className="font-medium">{usesFileStatus ? selectedFile.status : (jobStatus(selectedFile) ?? 'not started')}</span>
              </p>
            </div>
          )}
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default AdminConversionJobs;
